<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="small">
    <template #content>
      <!-- Social network picker -->
      <div
        :class="{
          'social-network-picker': true,
          'social-network-picker--disabled': isSaving || isAddingAccount,
        }"
      >
        <div class="standard-page-header standard-page-header--small-bottom-padding social-network-picker__header" style="margin-bottom: 20px">
          <h1 class="global-h1 standard-page-header__title">{{ !props.allCustomerSocialChannels.length ? "Add Social Accounts" : "Accounts to Use" }}</h1>
        </div>

        <div v-if="!props.allCustomerSocialChannels.length">
          <p class="global-p" style="margin-bottom: 15px">Link your social accounts to start posting!</p>
        </div>
        <div v-else class="social-network-picker__list">
          <div v-for="channel in visibleCustomerChannels" :key="channel.channelId" class="social-network-picker__row">
            <SrpCheckbox class="social-network-picker__checkbox" size="medium" :isDisabled="isChannelSelectionLocked(channel)">
              <template #input>
                <input type="checkbox" v-model="_selectedSocialChannels" :value="channel.channelType" :disabled="isChannelSelectionLocked(channel)" />
              </template>
              <template #text>
                <div class="icon-and-name-snippet">
                  <img class="icon-and-name-snippet__icon" :src="`/images/social-logo-${channel.channelType.toLowerCase()}.svg`" :alt="capitalizeSocialNetworkName(channel.channelType)" />
                  <span class="icon-and-name-snippet__name">
                    {{ capitalizeSocialNetworkName(channel.channelType) }}
                  </span>
                </div>
              </template>
            </SrpCheckbox>
            <div v-if="channelNeedsAuth(channel)" class="social-network-picker__trailing">
              <span class="channel__status-badge">Auth Needed</span>
            </div>
          </div>
        </div>
        <div v-if="hasAuthNeededChannels" class="social-network-picker__reauth-wrap">
          <SrpButton color="red" @click="handleAddSocialAccount" :isDisabled="isAddingAccount">
            <template #icon>
              <LoadingIcon v-if="isAddingAccount" :size="20" />
              <IconEmbedded v-else name="warning_2-5" :size="18" />
            </template>
            {{ isAddingAccount ? "Redirecting..." : "Re-Auth Needed" }}
          </SrpButton>
        </div>
        <!-- / Social network picker -->

        <NoteWithIcon v-if="props.allCustomerSocialChannels.length && !hasAnyPostTarget" class="social-network-picker__note-with-icon" style="margin-bottom: -20px">
          <template #text>
            <h6 class="global-h6">At least one account must be selected.</h6>
          </template>
        </NoteWithIcon>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <SrpButton @click="handleAddSocialAccount" :fill="props.allCustomerSocialChannels.length ? 'outlined' : 'solid'" :isDisabled="isAddingAccount">
        <template #icon>
          <LoadingIcon v-if="isAddingAccount" :size="22" />
          <IconEmbedded v-else name="plus_2-5" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Add Accounts
      </SrpButton>

      <SrpButton
        v-if="props.allCustomerSocialChannels.length"
        @click="
          async () => {
            await save();
            closeModal();
          }
        "
        :isDisabled="isSaving || !hasAnyPostTarget"
      >
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="22" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Done
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { capitalizeSocialNetworkName } from "@contracts/socialNetworks";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Types
import { SocialChannels } from "@contracts/socialNetworks";
import { SocialChannelStatus, SocialMediaPostData } from "@contracts/shareStop";
import type { SocialChannelData } from "@contracts/socialMediaSettings";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import { isChannelAuthNeeded } from "@logic/SocialMediaPost";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    post: SocialMediaPostData;
    allCustomerSocialChannels: Array<SocialChannelData>;
    selectedSocialChannels: Array<SocialChannels> | null;
  }>(),
  {
    allCustomerSocialChannels: () => [],
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "save", selectedSocialChannels: Array<SocialChannels>): void;
  (e: "addSocialAccount"): void;
}>();

const visibleCustomerChannels = computed(() => props.allCustomerSocialChannels.filter(ch => ch.status !== SocialChannelStatus.Deleted));
const hasAuthNeededChannels = computed(() => visibleCustomerChannels.value.some(channelNeedsAuth));

function channelNeedsAuth(channel: SocialChannelData): boolean {
  return isChannelAuthNeeded(channel.status);
}

function isChannelSelectionLocked(channel: SocialChannelData): boolean {
  return channelNeedsAuth(channel) && !_selectedSocialChannels.value.includes(channel.channelType);
}

const hasAnyPostTarget = computed(() => _selectedSocialChannels.value.length > 0);

// Checkbox values ============================================================
const _selectedSocialChannels = ref<Array<SocialChannels>>([]);

onMounted(() => {
  const initial = props.selectedSocialChannels || [];
  _selectedSocialChannels.value = initial.filter(ch => visibleCustomerChannels.value.some(channel => channel.channelType === ch));
});

watch(
  () => props.isVisible,
  isVisible => {
    if (!isVisible) isAddingAccount.value = false;
  }
);

// Add social account =========================================================
const isAddingAccount = ref<boolean>(false);

async function handleAddSocialAccount() {
  isAddingAccount.value = true;
  emit("addSocialAccount");
}

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  isSaving.value = true;
  const updatedPost = await ShareStopSummaryRepo.updatePost({
    ...props.post,
    postToAccounts: _selectedSocialChannels.value,
  });
  isSaving.value = false;

  emit("save", updatedPost.postToAccounts);
}
</script>

<style scoped lang="scss">
// Icon and name snippet ======================================================
.icon-and-name-snippet {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  &__icon {
    width: 23px;
    margin-right: 5px;
  }
}

.channel__status-badge {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(212, 51, 51, 0.1);
  color: #b42318;
  border: 1px solid rgba(180, 35, 24, 0.3);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

// Social network picker ======================================================
.social-network-picker {
  &__list {
    gap: 7px;
    padding: 0;
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
    width: 100%;
  }

  &__trailing {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;

    .channel__status-badge {
      margin-left: 0;
    }
  }

  &__reauth-wrap {
    margin-top: 12px;
  }
}
</style>
